/**
 * Utility functions for sessionStorage operations
 * Centralizes storage access to reduce duplication
 */

/**
 * Gets user details from sessionStorage
 * @returns Parsed user details object or empty object if not found
 */
export function getUserDetails(): any {
  try {
    const userDetails = sessionStorage.getItem('userDetails');
    return userDetails ? JSON.parse(userDetails) : {};
  } catch {
    return {};
  }
}

/**
 * Gets program ID from sessionStorage
 * @returns Program ID as number or null if not found
 */
export function getProgramId(): number | null {
  try {
    const programId = sessionStorage.getItem('programId');
    if (!programId) {
      return null;
    }
    const parsed = JSON.parse(programId);
    return parsed ? Number(parsed) : null;
  } catch {
    return null;
  }
}

/**
 * Gets staff info from sessionStorage
 * @returns Parsed staff info object or empty object if not found
 */
export function getStaffInfo(): any {
  try {
    const staffInfo = sessionStorage.getItem('staffInfo');
    return staffInfo ? JSON.parse(staffInfo) : {};
  } catch {
    return {};
  }
}

/**
 * Sets a value in sessionStorage
 * @param key - Storage key
 * @param value - Value to store (will be JSON stringified)
 */
export function setStorageItem(key: string, value: any): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error setting ${key} in sessionStorage:`, error);
  }
}

/**
 * Gets the selected location from sessionStorage
 * @returns Location object { id, locationName, addressLine } or null
 */
export function getSelectedLocation(): { id: string; locationName: string; addressLine: string } | null {
  try {
    const location = sessionStorage.getItem('selectedLocation');
    return location ? JSON.parse(location) : null;
  } catch {
    return null;
  }
}

/**
 * Locations assigned to the signed-in staff (from token exchange / refreshed mid-session).
 */
export function getStaffAssignedLocations(): Array<{
  id: string;
  locationName: string;
  addressLine: string;
}> {
  try {
    const raw = sessionStorage.getItem('tokenExchangeResponse');
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as { locations?: any[] };
    const locations = Array.isArray(parsed?.locations) ? parsed.locations : [];
    return locations
      .map((location) => ({
        id: String(location?.id ?? '').trim(),
        locationName: String(location?.locationName ?? '').trim(),
        addressLine: String(location?.addressLine ?? '').trim(),
      }))
      .filter((location) => !!location.id);
  } catch {
    return [];
  }
}

/**
 * Update assigned locations in the cached token-exchange payload (no re-login required).
 */
export function setStaffAssignedLocations(
  locations: Array<{ id: string; locationName: string; addressLine: string }>
): void {
  try {
    const raw = sessionStorage.getItem('tokenExchangeResponse');
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    parsed['locations'] = locations.map((location) => ({
      id: location.id,
      locationName: location.locationName,
      addressLine: location.addressLine,
    }));
    sessionStorage.setItem('tokenExchangeResponse', JSON.stringify(parsed));
  } catch (error) {
    console.error('Error setting staff assigned locations in sessionStorage:', error);
  }
}

/**
 * Persist selected location for the current session.
 */
export function setSelectedLocation(location: {
  id: string;
  locationName: string;
  addressLine: string;
}): void {
  try {
    sessionStorage.setItem('selectedLocation', JSON.stringify(location));
  } catch (error) {
    console.error('Error setting selectedLocation in sessionStorage:', error);
  }
}

/**
 * Gets a value from sessionStorage
 * @param key - Storage key
 * @param defaultValue - Default value if key not found
 * @returns Parsed value or default value
 */
export function getStorageItem<T>(key: string, defaultValue: T): T {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * @deprecated Use PermissionService.isAdministrator() instead.
 */
export function isAdminUser(): boolean {
  const tokenExchange = getStorageItem<{ role?: string } | null>('tokenExchangeResponse', null);
  const role = tokenExchange?.role ?? getStaffInfo()?.role;
  const normalized = role?.toLowerCase();

  return normalized === 'administrator';
}


